import React, { cloneElement, isValidElement, useId } from "react";
import { Popper, Box, Grow, IconButton, SxProps, Theme } from "@mui/material";
import { PopperPlacementType } from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { useTranslation } from "next-i18next";
import { IconClose } from "src/components/icons-mui/close";
import { SimpleRichText } from "src/components-mui/common/rich-text/simple-rich-text";
import { useToggleWithAnchor } from "src/theme-mui/components/popper/hooks/use-toggle-with-anchor";
import { ILink } from "src/types/common";
import { ArrowLink } from "../arrow-link/arrow-link";

const getArrowStyles = (placement: PopperPlacementType) => {
    const styles = {
        top: { "--popper-arrow-shadow": "1px 1px 0px 0 var(--mui-palette-gray-200)", bottom: "-2px" },
        bottom: {
            "--popper-arrow-shadow": "-1px -1px 0px 0 var(--mui-palette-gray-200)",
            top: "-2px",
        },
        right: {},
        left: {},
    } as const;
    return styles[placement.split("-")[0] as keyof typeof styles] || {};
};

export interface IWfPopoverProps {
    content: string;
    PopoverTrigger: React.ReactElement<{
        ref?: React.Ref<HTMLElement>;
        onClick?: () => void;
        "aria-haspopup": string;
        "aria-expanded": boolean;
        "aria-controls": string;
    }>;
    placement?: "top" | "bottom";
    link?: ILink | null;
    sxPopper?: SxProps<Theme>;
}

/**
 * This Popover uses `role="dialog"` because it contains interactive content
 * (close button, optional link) and is anchored to a trigger element.
 * According to WAI-ARIA, a popover with interactive elements is treated
 * as a small non-modal dialog. `aria-haspopup="dialog"` and `aria-controls`
 * on the trigger ensure screen readers announce and link the elements properly.
 */

export function WfPopover({ PopoverTrigger, content, link, placement = "bottom", sxPopper }: IWfPopoverProps) {
    const [arrowRef, setArrowRef] = React.useState<HTMLSpanElement | null>(null);
    const { t } = useTranslation("common");
    const { anchorRef, handleClick, handleClose, isOpen: open } = useToggleWithAnchor();
    const popoverId = useId();

    return (
        <>
            {isValidElement(PopoverTrigger)
                ? cloneElement(PopoverTrigger, {
                      ref: anchorRef,
                      onClick: handleClick,
                      "aria-haspopup": "dialog",
                      "aria-expanded": open,
                      "aria-controls": popoverId,
                  })
                : null}

            <Popper
                role="dialog"
                id={popoverId}
                open={open}
                anchorEl={anchorRef.current}
                disablePortal={false}
                transition
                placement={placement}
                sx={{ zIndex: "tooltip", ...sxPopper }}
                modifiers={[
                    {
                        name: "flip",
                        enabled: true,
                    },
                    {
                        name: "arrow",
                        enabled: true,
                        options: {
                            element: arrowRef,
                        },
                    },
                    {
                        name: "offset",
                        options: {
                            offset: [0, 8],
                        },
                    },
                ]}
            >
                {({ placement, TransitionProps }) => {
                    return (
                        <ClickAwayListener onClickAway={handleClose}>
                            <Grow
                                {...TransitionProps}
                                style={{
                                    transformOrigin: placement === "top" ? "center bottom" : "center top",
                                }}
                            >
                                <Box sx={{ zIndex: 1, position: "relative", backgroundColor: "white" }}>
                                    <Box
                                        ref={(element: HTMLSpanElement | null) => setArrowRef(element)}
                                        sx={{
                                            width: "8px",
                                            height: "8px",
                                            position: "absolute",
                                            bottom: "0",
                                            zIndex: 1,
                                            ...getArrowStyles(placement),
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                transform: "rotate(45deg)",
                                                backgroundColor: "white",
                                                position: "absolute",
                                                width: "100%",
                                                height: "100%",
                                                boxShadow: "var(--popper-arrow-shadow)",
                                            }}
                                        />
                                    </Box>

                                    <Box
                                        sx={{
                                            border: "1px solid",
                                            borderColor: "gray.200",
                                            borderRadius: "6px",
                                            position: "relative",
                                            px: 2,
                                            py: 2,
                                            pr: 7,
                                            width: "320px",
                                        }}
                                    >
                                        <>
                                            <IconButton
                                                variant="ghost"
                                                sx={{
                                                    position: "absolute",
                                                    top: "8px",
                                                    right: "8px",
                                                    borderRadius: "6px",
                                                }}
                                                size="extraSmall"
                                                aria-label={t("close")}
                                                onClick={handleClose}
                                            >
                                                <IconClose sx={{ width: "16px", height: "16px" }} />
                                            </IconButton>
                                            <Box sx={{ display: "flex", alignItems: "center", minHeight: "16px" }}>
                                                <SimpleRichText fontSize="sm" text={content} />
                                            </Box>

                                            {link && (
                                                <ArrowLink
                                                    sx={{ display: "inline-block", marginTop: 2 }}
                                                    href={link.url}
                                                    target={link.target}
                                                    color="green"
                                                >
                                                    {link.name}
                                                </ArrowLink>
                                            )}
                                        </>
                                    </Box>
                                </Box>
                            </Grow>
                        </ClickAwayListener>
                    );
                }}
            </Popper>
        </>
    );
}
